import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import type {
    InventoryMetricsTimeseriesRequest,
    InventoryMetricsTimeseriesResponse,
    ReportingComparison,
    InventoryReportingAdjustmentType,
} from '../../types/reporting';
import type { InventoryPartType } from '../../types/inventory';

export interface UseInventoryReportingTimeseriesOptions {
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    locationIds?: string[];
    preset?: string;
    types?: InventoryReportingAdjustmentType[];
    tagIds?: string[];
    binIds?: string[];
    partTypes?: InventoryPartType[];
    supplierIds?: string[];
    search?: string;
    restockAlert?: boolean;
    comparison?: ReportingComparison;
    enabled?: boolean;
}

const getInventoryReportingTimeseries = async (
    sessionId: string,
    input: InventoryMetricsTimeseriesRequest,
): Promise<InventoryMetricsTimeseriesResponse> => {
    const { data } = await axios.post<InventoryMetricsTimeseriesResponse>(
        `${API_URL}/reporting/inventory/timeseries`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useInventoryReportingTimeseries = (
    startDate: string,
    endDate: string,
    options: UseInventoryReportingTimeseriesOptions = {},
) => {
    const sessionContext = useContext(SessionContext);
    const {
        granularity = 'week',
        locationIds,
        preset,
        types,
        tagIds,
        binIds,
        partTypes,
        supplierIds,
        search,
        restockAlert,
        comparison,
        enabled = true,
    } = options;

    return useQuery({
        queryKey: [
            'reporting',
            'inventory',
            'timeseries',
            startDate,
            endDate,
            granularity,
            locationIds,
            preset,
            types,
            tagIds,
            binIds,
            partTypes,
            supplierIds,
            search,
            restockAlert,
            comparison,
        ],
        queryFn: () =>
            getInventoryReportingTimeseries(sessionContext?.sessionId ?? '', {
                startDate,
                endDate,
                granularity,
                locationIds: locationIds?.length ? locationIds : undefined,
                preset,
                types: types?.length ? types : undefined,
                tagIds: tagIds?.length ? tagIds : undefined,
                binIds: binIds?.length ? binIds : undefined,
                partTypes: partTypes?.length ? partTypes : undefined,
                supplierIds: supplierIds?.length ? supplierIds : undefined,
                search: search || undefined,
                restockAlert,
                // When comparing: send a valid comparison. Otherwise omit / false (e.g. custom ranges).
                ...(comparison
                    ? { includeComparison: true as const, comparison }
                    : { includeComparison: false as const }),
            }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
